// Azure Static Web Apps built-in authentication.
//
// We deliberately do NOT use MSAL here. SWA fronts the app with its own auth
// layer: the user hits /.auth/login/aad, SWA handles the Entra ID handshake,
// and every subsequent /api/* call is forwarded to the linked Function App
// with an x-ms-client-principal header. That's the "auth passthrough" the spec
// describes in Section 1 — the API never has to validate a bearer token itself.

/** Shape returned by SWA's /.auth/me endpoint. */
export interface ClientPrincipal {
  identityProvider: string;
  userId: string; // Entra ID object id — the FK used by GymProfile.userId
  userDetails: string; // work email (the only identity claim Entra gives us here)
  userRoles: string[]; // "anonymous", "authenticated", plus any custom roles
  claims?: { typ: string; val: string }[];
}

export const ROLE_COACH = "coach";
export const ROLE_ADMIN = "admin";
export const ROLE_CAFETERIA = "cafeteria";

/**
 * Reads the current login state. Returns null when signed out.
 * Under `vite dev` (no SWA CLI) /.auth/me does not exist, so this resolves to
 * null and the app renders its signed-out state — use `npm run swa` to test
 * the real login flow.
 */
export async function getClientPrincipal(): Promise<ClientPrincipal | null> {
  try {
    const res = await fetch("/.auth/me", { credentials: "include" });
    if (!res.ok) return null;

    const payload = (await res.json()) as { clientPrincipal: ClientPrincipal | null };
    return payload.clientPrincipal ?? null;
  } catch {
    // Network error or non-SWA host — treat as signed out rather than crashing.
    return null;
  }
}

/** Sends the user to Entra ID, returning to `redirect` afterwards. */
export function login(redirect: string = window.location.pathname): void {
  window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(redirect)}`;
}

export function logout(): void {
  window.location.href = "/.auth/logout?post_logout_redirect_uri=/";
}

export function hasRole(principal: ClientPrincipal | null, role: string): boolean {
  return principal?.userRoles.includes(role) ?? false;
}
